import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import classNames from 'classnames';
import React, {FC, memo, useMemo, useState} from 'react';
import Measure from 'react-measure';

import * as ReportsTypes from '../../state/reports/types';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankConfigActions from '../../state/views/panelBankConfig/actions';
import {usePanelBankSectionConfig} from '../../state/views/panelBankSectionConfig/hooks';
import * as PanelSettingsViewTypes from '../../state/views/panelSettings/types';
import * as SectionViewTypes from '../../state/views/section/types';
import {getSectionsWithVisiblePanels} from '../../util/panelbank';
import {GRID_ITEM_MARGIN, GRID_ROW_HEIGHT} from '../../util/panelbankGrid';
import {keyExistsInQueryString} from '../../util/query-string';
import {CreateReportModal} from '../CreateReportModal';
import {
  KeyInfoQueryContextProvider,
  useKeyInfoQueryContext,
} from '../MultiRunWorkspace/KeyInfoQueryContext';
import {PanelBankGridSection} from '../PanelBank/PanelBankGridSection';
import {OrganizedSettings} from '../PanelBank/types';
import {RunSets} from '../RunSets';
import {PanelsSharedRefsContextProvider} from '../Views/contexts/PanelsSharedRefsContext';
import {SemanticLegendSettings} from '../WorkspaceDrawer/Settings/types';
import {useReportSectionProps} from './hooks';
import {ReportPanelGridActions} from './ReportPanelGridActions';
import * as S from './ReportPanelGridSection.styles';
import {IMPORT_PANEL_QS, REPORT_PANEL_GRID_MENU} from './types';

export type ReportPanelGridSectionProps = {
  className?: string;
  entityName: string;
  projectName: string;
  panelSettingsRef: PanelSettingsViewTypes.Ref;
  sectionRef: SectionViewTypes.Ref;
  viewRef: ReportsTypes.ReportViewRef;
  readOnly?: boolean;
  disableRunLinks?: boolean;
  onRunSetHeightChange?(): void;
  semanticLegendSettings?: SemanticLegendSettings;
};

const ReportPanelGridSectionComp: FC<ReportPanelGridSectionProps> = props => {
  const {
    className,
    entityName,
    projectName,
    panelSettingsRef,
    sectionRef,
    viewRef,
    readOnly,
    disableRunLinks,
    onRunSetHeightChange,
  } = props;
  const {activePanelRefs, sectionPart} = useReportSectionProps(props);

  const [panelBankWidth, setPanelBankWidth] = useState(0);
  const movePanel = ViewHooks.useViewAction(
    sectionPart.panelBankConfigRef,
    PanelBankConfigActions.movePanel
  );
  // make sure this is well memoized or the panel components will rerender every update
  // reports has not been migrated to use the new settings structure yet
  const panelSettings = ViewHooks.usePart(panelSettingsRef);
  const inheritedSettings: OrganizedSettings = useMemo(() => {
    return {
      linePlot: panelSettings,
    };
  }, [panelSettings]);

  const useExportableSections = () => {
    const panelBankSectionConfig = usePanelBankSectionConfig(
      sectionPart.panelBankSectionConfigRef
    );
    return useMemo(
      () => getSectionsWithVisiblePanels([panelBankSectionConfig]),
      [panelBankSectionConfig]
    );
  };

  // To control the grid panel menu popups
  const initialOpenedMenu = keyExistsInQueryString(IMPORT_PANEL_QS)
    ? REPORT_PANEL_GRID_MENU.IMPORT_PANEL
    : undefined;
  const [openedMenu, setOpenedMenu] = useState<
    REPORT_PANEL_GRID_MENU | undefined
  >(initialOpenedMenu);

  // Make a history keys query so the state is in redux.
  // This allows query merging for panels in the section.
  const {keyInfoQuery} = useKeyInfoQueryContext();
  if (keyInfoQuery.error) {
    return (
      <div className="report-section">
        <S.EmptyReportSection>
          This set of panels contains runs from a private project, which cannot
          be shown in this report
        </S.EmptyReportSection>
      </div>
    );
  }

  return (
    <div
      className={classNames('report-section', 'panel-bank', className)}
      data-test="report-panel-grid-section">
      <Measure
        bounds
        onResize={contentRect => {
          setPanelBankWidth(contentRect.bounds ? contentRect.bounds.width : 0);
        }}>
        {({measureRef}) => (
          <>
            <div className="grid-section-wrapper">
              <div ref={measureRef}>
                {panelBankWidth > 0 && (
                  <PanelBankGridSection
                    activePanelRefs={activePanelRefs}
                    disableRunLinks={disableRunLinks}
                    gridContainerPadding={[0, 0]}
                    gridItemMargin={[-1, -1]}
                    gridRowHeight={GRID_ROW_HEIGHT + GRID_ITEM_MARGIN[1]}
                    hideEmptyWatermark
                    historyKeyInfo={
                      !keyInfoQuery.loading && keyInfoQuery.error == null
                        ? keyInfoQuery.historyKeyInfo
                        : undefined
                    }
                    inactivePanelRefs={[]}
                    inheritedSettings={inheritedSettings}
                    movePanelBetweenSections={movePanel}
                    onClickGrid={() => {
                      if (openedMenu !== REPORT_PANEL_GRID_MENU.ADD_PANEL) {
                        // HAX: we need the setTimeout to ensure this runs
                        // after the add vis Popup's onClose handler
                        setTimeout(() =>
                          setOpenedMenu(REPORT_PANEL_GRID_MENU.ADD_PANEL)
                        );
                      }
                    }}
                    panelBankSectionConfigRef={
                      sectionPart.panelBankSectionConfigRef
                    }
                    panelBankWidth={panelBankWidth}
                    readOnly={readOnly}
                    shouldTurnOnComments
                    showGridDots={!readOnly}
                    shouldRenderContent
                    // TODO: re-enable this??
                    // singleRun={singleRun}
                  />
                )}
              </div>
            </div>
            {!readOnly && (
              <ReportPanelGridActions
                entityName={entityName}
                projectName={projectName}
                sectionPart={sectionPart}
                viewRef={viewRef}
                openedMenu={openedMenu}
                setOpenedMenu={setOpenedMenu}
              />
            )}
            {!(readOnly && sectionPart.hideRunSets) && (
              <RunSets
                entityName={entityName}
                projectName={projectName}
                sectionRef={sectionRef}
                readOnly={readOnly}
                disableRunLinks={disableRunLinks}
                onHeightChange={onRunSetHeightChange}
                isInReport={true}
              />
            )}
          </>
        )}
      </Measure>
      <CreateReportModal
        useExportableSections={useExportableSections}
        inheritedSettings={inheritedSettings}
      />
    </div>
  );
};

export const ReportPanelGridSection = memo(
  (props: ReportPanelGridSectionProps) => {
    const {sectionPart} = useReportSectionProps(props);
    return (
      <KeyInfoQueryContextProvider
        entityName={props.entityName}
        runSetRefs={sectionPart.runSetRefs}
        customRunNamesRef={sectionPart.customRunNamesRef}
        allowMultiRunsetHistoryKeys={true}>
        <PanelsSharedRefsContextProvider
          customRunColorsRef={sectionPart.customRunColorsRef}
          customRunNamesRef={sectionPart.customRunNamesRef}
          semanticLegendRef={sectionPart.semanticLegendRef}
          runSetRefs={sectionPart.runSetRefs}>
          <ReportPanelGridSectionComp {...props} />
        </PanelsSharedRefsContextProvider>
      </KeyInfoQueryContextProvider>
    );
  },
  makePropsAreEqual({
    name: 'ReportPanelGridSection',
    deep: ['config', 'customRunColors', 'panelSettings', 'queries'],
    ignoreFunctions: false,
    debug: false,
    verbose: true,
  })
);
